import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type PaymentAccount = Database['public']['Tables']['payment_accounts']['Row'];
type Transaction = Database['public']['Tables']['transactions']['Row'];

interface AccountUsage {
  accountId: number;
  count: number;
  lastUsed: string;
  transactionType: 'income' | 'expense';
}

interface UseSmartAccountDefaultsReturn {
  getDefaultAccount: (transactionType: 'income' | 'expense') => Promise<number | null>;
  recordAccountUsage: (accountId: number, transactionType: 'income' | 'expense') => Promise<void>;
  getUsageStats: () => AccountUsage[];
}

export const useSmartAccountDefaults = (): UseSmartAccountDefaultsReturn => {
  const [usageStats, setUsageStats] = useState<AccountUsage[]>([]);

  // Carregar estatísticas de uso das contas
  useEffect(() => {
    const loadUsageStats = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Buscar transações recentes para analisar padrões de uso
        const { data: recentTransactions, error } = await supabase
          .from('transactions')
          .select('payment_account_id, type, created_at')
          .eq('user_id', user.id)
          .not('payment_account_id', 'is', null)
          .order('created_at', { ascending: false })
          .limit(100);

        if (error) throw error;

        // Analisar padrões de uso
        const accountUsage: { [key: string]: AccountUsage } = {};

        recentTransactions?.forEach(transaction => {
          const accountId = transaction.payment_account_id;
          const transactionType = transaction.type as 'income' | 'expense';
          const key = `${accountId}_${transactionType}`;

          if (!accountUsage[key]) {
            accountUsage[key] = {
              accountId: accountId!,
              count: 0,
              lastUsed: transaction.created_at,
              transactionType
            };
          }

          accountUsage[key].count++;
          accountUsage[key].lastUsed = transaction.created_at;
        });

        const stats = Object.values(accountUsage)
          .sort((a, b) => {
            // Priorizar por frequência de uso, depois por data de uso mais recente
            if (a.count !== b.count) return b.count - a.count;
            return new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime();
          });

        setUsageStats(stats);
      } catch (error) {
        console.error('Erro ao carregar estatísticas de uso:', error);
      }
    };

    loadUsageStats();

    // Configurar subscription para atualizações em tempo real
    const channel = supabase
      .channel(`transaction_usage_changes_${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'transactions'
        },
        () => {
          loadUsageStats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const getDefaultAccount = async (transactionType: 'income' | 'expense'): Promise<number | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // 1. Buscar contas compatíveis
      const { data: accounts, error } = await supabase
        .from('payment_accounts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!accounts || accounts.length === 0) return null;

      // 2. Filtrar contas compatíveis com o tipo de transação
      const compatibleAccounts = accounts.filter(account => {
        const accountType = account.type?.toLowerCase();

        if (transactionType === 'income') {
          return ['checking', 'savings', 'digital_wallet', 'pix'].includes(accountType || '');
        } else {
          return ['checking', 'savings', 'credit_card', 'digital_wallet', 'pix'].includes(accountType || '');
        }
      });

      if (compatibleAccounts.length === 0) return null;

      // 3. Buscar estatísticas de uso para este tipo de transação
      const typeStats = usageStats.filter(stat => stat.transactionType === transactionType);

      if (typeStats.length > 0) {
        // 4. Priorizar conta mais usada para este tipo de transação
        const mostUsed = typeStats.find(stat =>
          compatibleAccounts.some(acc => acc.id === stat.accountId)
        );

        if (mostUsed) return mostUsed.accountId;
      }

      // 5. Se não houver estatísticas, usar a primeira conta compatível
      return compatibleAccounts[0].id;
    } catch (error) {
      console.error('Erro ao obter conta padrão:', error);
      return null;
    }
  };

  const recordAccountUsage = async (accountId: number, transactionType: 'income' | 'expense') => {
    try {
      // Esta função poderia ser usada para registrar explicitamente o uso
      // quando uma transação é criada, mas já estamos usando o subscription
      // então não é necessário fazer nada adicional aqui

      // Poderíamos atualizar as estatísticas localmente para resposta imediata
      const existingIndex = usageStats.findIndex(
        stat => stat.accountId === accountId && stat.transactionType === transactionType
      );

      if (existingIndex >= 0) {
        const updatedStats = [...usageStats];
        updatedStats[existingIndex] = {
          ...updatedStats[existingIndex],
          count: updatedStats[existingIndex].count + 1,
          lastUsed: new Date().toISOString()
        };
        setUsageStats(updatedStats);
      }
    } catch (error) {
      console.error('Erro ao registrar uso da conta:', error);
    }
  };

  const getUsageStats = (): AccountUsage[] => usageStats;

  return {
    getDefaultAccount,
    recordAccountUsage,
    getUsageStats
  };
};